import React from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { getDestinationIconByCode } from "helpers/destinations/getDestinationIcon";
import { getSourceIconByCode } from "helpers/Sources/getSourceIcon";

import {
    IconTile,
    ItemInfo,
    ItemMeta,
    ItemName,
    ListBody,
    Row,
    RowMain,
} from "../../components/SelectableList/SelectableList.styled";
import StepHeader from "../../components/StepHeader";
import {
    Card,
    CountPill,
    SectionHeader,
    SectionTitle,
    StatusDot,
} from "../../SignalWizard.styled";
import {
    ErrorText,
    FieldLabel,
    HelpNote,
    NameCard,
    NameInput,
    SummaryGrid,
} from "./CreateTrackerStep.styled";

const HELP_DOCUMENT_URL =
    "https://signalsight.io/docs/facebook-capi-setup.html#creating-tracker";

const SummaryList = ({ title, items, typeKey, getIcon }) => (
    <div>
        <SectionHeader>
            <SectionTitle>
                <span>{title}</span>
                <StatusDot />
            </SectionTitle>
            <CountPill>{items.length}</CountPill>
        </SectionHeader>
        <Card>
            <ListBody>
                {items.map((item) => (
                    <Row key={item.id} sx={{ cursor: "default" }}>
                        <RowMain>
                            <IconTile $size={32}>
                                {getIcon(item) && (
                                    <img src={getIcon(item)} alt="" />
                                )}
                            </IconTile>
                            <ItemInfo>
                                <ItemName>{item.name}</ItemName>
                                <ItemMeta>{item?.[typeKey]?.name}</ItemMeta>
                            </ItemInfo>
                        </RowMain>
                    </Row>
                ))}
            </ListBody>
        </Card>
    </div>
);

const CreateTrackerStep = ({
    language,
    selectedSourceItems,
    selectedDestinationItems,
    trackerName,
    trackerNameError,
    onTrackerNameChange,
    onSubmitTracker,
}) => (
    <>
        <StepHeader
            language={language}
            stepNumber={3}
            title={
                language?.tracker_wizard?.name_your_tracker ??
                "Name your tracker"
            }
            description={
                language?.tracker_wizard
                    ?.trackers_diligently_collect_and_convey_crucial_interaction_insights_on_websites_and_both_online_and_offline_platforms ??
                "Trackers diligently collect and convey crucial interaction insights on websites and both online and offline platforms. This meticulous data gathering is instrumental in enhancing successful signal transmissions, optimizing conversions, and bolstering the efficacy of your strategic advertisements."
            }
        />

        <NameCard
            component="form"
            noValidate
            onSubmit={(event) => {
                event.preventDefault();
                onSubmitTracker();
            }}
        >
            <FieldLabel htmlFor="trackerTitle">
                {language?.create_new_tracker_modal?.tracker_name ??
                    "Tracker Name"}
            </FieldLabel>
            <NameInput
                id="trackerTitle"
                autoFocus
                value={trackerName}
                $hasError={Boolean(trackerNameError)}
                onChange={(event) => onTrackerNameChange(event.target.value)}
                placeholder={
                    language?.create_new_tracker_modal?.enter_tracker_name ??
                    "Enter Tracker Name"
                }
                inputProps={{ "data-cy": "wizard-tracker-name" }}
            />
            {trackerNameError && (
                <ErrorText data-cy="wizard-tracker-name-error">
                    {trackerNameError}
                </ErrorText>
            )}
            <HelpNote>
                <InfoOutlinedIcon />
                <span>
                    {language?.tracker_wizard?.please_refer_to ??
                        "Please refer to"}{" "}
                    <a
                        href={HELP_DOCUMENT_URL}
                        target="_blank"
                        rel="noreferrer"
                    >
                        {language?.tracker_wizard?.help_document ??
                            "help document"}
                    </a>{" "}
                    {language?.tracker_wizard?.for_tracker_creating ??
                        "for tracker creating screen details."}
                </span>
            </HelpNote>
        </NameCard>

        <SummaryGrid>
            <SummaryList
                title={
                    language?.tracker_wizard?.selected_sources ??
                    "Selected Sources"
                }
                items={selectedSourceItems}
                typeKey="source_type"
                getIcon={(source) =>
                    getSourceIconByCode(source?.source_type?.code)
                }
            />
            <SummaryList
                title={
                    language?.tracker_wizard?.selected_destinations ??
                    "Selected Destinations"
                }
                items={selectedDestinationItems}
                typeKey="destination_type"
                getIcon={(destination) =>
                    getDestinationIconByCode(
                        destination?.destination_type?.code,
                    )
                }
            />
        </SummaryGrid>
    </>
);

export default CreateTrackerStep;
